<script setup lang="ts">
    import { __ } from '../stores/expressions';

    import { DatePicker } from 'v-calendar';
    import 'v-calendar/style.css';
    import { nextTick, onMounted, ref, watch } from 'vue';
    import { CalendarIcon } from '@lucide/vue';
    import dayjs from 'dayjs';

    const formatDate = function (date: Date): string {
        return date.toISOString().split('T')[0];
    };

    function toLocalDate(dateString: string): Date {
        if (!dateString) return new Date();
        return dayjs(dateString).hour(12).toDate();
    }

    const validDate = function (date: string): boolean {
        return (
            String(date).length === 10 &&
            !!String(date).match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/) &&
            !isNaN(new Date(date).getTime())
        );
    };

    const format = function (event: KeyboardEvent): string {
        const target = event.target as HTMLInputElement;
        const key = event.key as string;

        if (['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].includes(key)) {
            if (target.value.length === 4) {
                target.value += '-';
            } else if (target.value.length === 7) {
                target.value += '-';
            }
        } else if (key === 'Backspace') {
            if (target.value.length === 6 || target.value.length === 9) {
                target.value = target.value.slice(0, -1);
            }
        }

        return target.value;
    };

    const emit = defineEmits<{
        (e: 'update:modelValue', value: { start?: string; end?: string }): void;
    }>();

    const props = defineProps<{
        modelValue: {
            start?: string;
            end?: string;
        };
    }>();

    const range = ref({
        start: props.modelValue.start ? toLocalDate(props.modelValue.start) : new Date(),
        end: props.modelValue.end ? toLocalDate(props.modelValue.end) : new Date(),
    });

    const calendar = ref<InstanceType<typeof DatePicker> | null>(null);

    const start = ref(formatDate(range.value.start));
    const end = ref(formatDate(range.value.end));

    // Flag to suppress re-emission when parent props drive an internal update.
    // Without this, prop changes → start/end update → emit back to parent → feedback loop.
    let suppressEmit = false;

    watch(
        () => props.modelValue,
        (newValue) => {
            suppressEmit = true;
            start.value = newValue.start || '';
            end.value = newValue.end || '';
            nextTick(() => {
                suppressEmit = false;
            });
        }
    );

    watch(
        () => start.value,
        (newStart) => {
            if (validDate(newStart)) {
                if (new Date(newStart) > range.value.end) {
                    range.value.end = toLocalDate(newStart);
                }

                range.value = {
                    start: toLocalDate(newStart),
                    end: range.value.end,
                };

                if (!suppressEmit) {
                    emit('update:modelValue', {
                        start: start.value,
                        end: end.value,
                    });
                }
            }
        }
    );

    watch(
        () => end.value,
        (newEnd) => {
            if (validDate(newEnd)) {
                range.value = {
                    start: range.value.start,
                    end: toLocalDate(newEnd),
                };

                if (!suppressEmit) {
                    emit('update:modelValue', {
                        start: start.value,
                        end: end.value,
                    });
                }
            }
        }
    );

    watch(
        () => range.value,
        (newRange) => {
            let formattedStart = formatDate(newRange.start);
            if (formattedStart && start.value !== formattedStart) {
                start.value = formattedStart;
            }

            let formattedEnd = formatDate(newRange.end);
            if (formattedEnd && end.value !== formattedEnd) {
                end.value = formattedEnd;
            }

            if (calendar.value) {
                calendar.value.move(formattedEnd);
            }
        }
    );

    onMounted(() => {
        if (calendar.value && end.value) {
            calendar.value.move(end.value);
        }

        // The inputs fall back to today, so a parent without dates must receive what is shown.
        if (!props.modelValue.start || !props.modelValue.end) {
            emit('update:modelValue', { start: start.value, end: end.value });
        }
    });
</script>

<template>
    <label for="start" class="Input-label capitalize">{{ __('date-from') }}</label>
    <div class="relative">
        <CalendarIcon class="size-5 absolute top-2 left-2 pointer-events-none" />
        <input
            id="start"
            v-model="start"
            type="tel"
            :placeholder="__('YYYY-MM-DD')"
            class="text-input pl-8 mb-5"
            @keydown="start = format($event)"
        />
    </div>
    <label for="end" class="Input-label capitalize">{{ __('date-to') }}</label>
    <div class="relative">
        <CalendarIcon class="size-5 absolute top-2 left-2 pointer-events-none" />
        <input
            id="end"
            v-model="end"
            type="tel"
            :placeholder="__('YYYY-MM-DD')"
            class="text-input mb-5 pl-8"
            @keydown="end = format($event)"
        />
    </div>
    <DatePicker
        ref="calendar"
        v-model.range="range"
        class="vcalendar-input"
        mode="date"
        :first-day-of-week="2"
    />
</template>
